
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Suggestion } from '@/components/statistics/PerformanceSuggestions';
import { DoctorStatisticsData } from '@/types/doctor-statistics';
import ROUTES from '@/routes-map';

export const useDoctorSuggestions = (
  statisticsData: DoctorStatisticsData,
  contentData?: {
    daysSinceLastStory?: number;
    daysSinceLastBeforeAfter?: number;
    reviewScore?: number;
    reviewCount?: number;
  }
): Suggestion[] => {
  const suggestions = useMemo(() => {
    const result: Suggestion[] = [];
    
    // Check if the doctor has active ads
    if (!statisticsData.isAdsActive) {
      result.push({
        id: uuidv4(),
        message: "You have no active ads. Create one to increase your visibility.",
        icon: 'trending-up',
        action: {
          label: "Create New Ad",
          link: `${ROUTES.DOCTOR.ADS}?tab=purchase`
        },
        severity: 'medium'
      });
    }
    
    // Check for ad performance
    const adPerformance = statisticsData.adPerformance;
    if (adPerformance && statisticsData.isAdsActive) {
      const conversionRate = (adPerformance.totalInquiries / adPerformance.totalImpressions) * 100;
      if (conversionRate < 1.0) {
        result.push({
          id: uuidv4(),
          message: "Your ad conversion rate is below average. Consider updating your visuals or message.",
          icon: 'trending-down',
          action: {
            label: "Update Ads",
            link: ROUTES.DOCTOR.ADS
          },
          severity: 'high'
        });
      }
    }
    
    // Content freshness checks
    if (contentData) {
      if (contentData.daysSinceLastStory && contentData.daysSinceLastStory > 7) {
        result.push({
          id: uuidv4(),
          message: `You haven't posted a story in ${contentData.daysSinceLastStory} days. Add one to reach more patients.`,
          icon: 'message-square',
          action: {
            label: "Add Story",
            link: ROUTES.DOCTOR.STORIES
          },
          severity: contentData.daysSinceLastStory > 14 ? 'high' : 'medium'
        });
      }
      
      if (contentData.daysSinceLastBeforeAfter && contentData.daysSinceLastBeforeAfter > 14) {
        result.push({
          id: uuidv4(),
          message: "Add new Before/After images to increase profile visibility.",
          icon: 'image',
          action: {
            label: "Upload Images",
            link: ROUTES.DOCTOR.BEFORE_AFTER
          },
          severity: 'medium'
        });
      }
      
      if (contentData.reviewScore && contentData.reviewCount) {
        if (contentData.reviewScore >= 4.5 && contentData.reviewCount < 10) {
          result.push({
            id: uuidv4(),
            message: "Your review score is excellent! Ask more satisfied patients to rate you.",
            icon: 'star',
            action: {
              label: "View Reviews",
              link: ROUTES.DOCTOR.REVIEWS
            },
            severity: 'low'
          });
        }
      }
    }
    
    // Patient conversion rate check
    if (statisticsData.conversionData) {
      const { plansAcceptedPercent } = statisticsData.conversionData;
      if (plansAcceptedPercent < 50) {
        result.push({
          id: uuidv4(),
          message: "Your treatment plan acceptance rate is below target. Review your pricing and communication strategy.",
          icon: 'chart-bar',
          severity: 'high'
        });
      }
    }
    
    return result;
  }, [statisticsData, contentData]);
  
  return suggestions;
};
